import React, { useContext, useMemo, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import TextAreaField from 'components/TextAreaField';
import { Field, useForm, useFormState } from 'react-final-form';
import BrokerPulsCheckActions from '../BrokerPulsCheckActions';
import RadioGroupField from 'components/RadioGroupField';
import {
  DEAL_CONTRACT_STATUS,
  DEAL_OFFER_STATUS,
  DEAL_OUTCOME,
  DEAL_STAGE,
} from 'models/DealRoom';
import DealContext from '../../../../providers/Deal';
import { DEAL_STATUS_LABEL } from 'data/dealStatus';
import DatepickerField from 'components/DatepickerField';
import LeadsField from '../../../ChangeDealStatusDrawer/components/LeadsField';
import { DOLLAR } from 'data/global';
import { addDollarPreffix, extractValueFromMoneyString } from 'utils';
import InputField from 'components/InputField';
import validations from 'validations';
import { Button } from 'ncoded-component-library';
import XcircleIcon from 'icons/Xcircle.icon';
import ListingContext from '../../../../providers/Listing/Listing.context';

import './NoLongerForSaleSection.styles.scss';
import './NoLongerForSaleSection.styles.responsive.scss';

type NoLongerForSaleSectionProps = {
  className?: string;
};

const NoLongerForSaleSection: React.FC<NoLongerForSaleSectionProps> = (
  props,
) => {
  const { className } = props;

  const classes = classNames('no-longer-for-sale-section', className);

  const [step, setStep] = useState<1 | 2>(1);

  const { leads } = useContext(ListingContext);
  const {
    dealRoom: { dealOutcome },
  } = useContext(DealContext);

  const { change, submit } = useForm();
  const { invalid, values: { stage } = {} } = useFormState();

  const { t } = useTranslation();

  const soldOrComplete = useMemo(
    () =>
      dealOutcome === DEAL_OUTCOME.SALE_CAMPAIGN
        ? DEAL_STAGE.COMPLETE
        : DEAL_STAGE.SOLD,
    [dealOutcome],
  );

  const stageOptions = useMemo(
    () => [
      {
        value: DEAL_STAGE.UNDER_OFFER,
        label: t(DEAL_STATUS_LABEL[DEAL_STAGE.UNDER_OFFER]),
      },
      {
        value: DEAL_STAGE.UNDER_CONTRACT,
        label: t(DEAL_STATUS_LABEL[DEAL_STAGE.UNDER_CONTRACT]),
      },
      {
        value: soldOrComplete,
        label: t(DEAL_STATUS_LABEL[soldOrComplete]),
      },
    ],
    [soldOrComplete, t],
  );

  const contractStatusOptions = useMemo(
    () =>
      Object.values(DEAL_CONTRACT_STATUS).map((option) => ({
        value: option,
        label: t(option),
      })),
    [t],
  );

  const offerStatusOptions = useMemo(
    () =>
      Object.values(DEAL_OFFER_STATUS).map((option) => ({
        value: option,
        label: t(option),
      })),
    [t],
  );

  return (
    <div className={classes}>
      <p className="steps">
        {t('stepsCurrentOf', { currentStep: step, steps: 2 })}
      </p>

      <p className="title">
        {t(step === 1 ? 'whatsTheStatus' : 'stillForSaleSectionTitle')}
      </p>

      {step === 1 ? (
        <>
          <RadioGroupField
            name="stage"
            className="stage-options"
            options={stageOptions}
            styled
          />

          {stage === DEAL_STAGE.UNDER_OFFER && (
            <RadioGroupField
              name="offerStatus"
              options={offerStatusOptions}
              label={t('offerStatus')}
            />
          )}

          {stage === DEAL_STAGE.UNDER_CONTRACT && (
            <RadioGroupField
              name="contractStatus"
              options={contractStatusOptions}
              label={t('contractStatus')}
            />
          )}

          {(stage === DEAL_STAGE.UNDER_OFFER ||
            stage === DEAL_STAGE.UNDER_CONTRACT) &&
            !!leads.length && (
              <>
                <p className="select-lead">
                  {t('selectWhichResolveLeadMadeTheOffer')}
                </p>
                <LeadsField leads={leads} />
              </>
            )}

          {(stage === DEAL_STAGE.SOLD || stage === DEAL_STAGE.COMPLETE) && (
            <>
              <Field
                label={t('settlementDate')}
                name="settlementDate"
                component={DatepickerField}
              />
              <Field
                label={t('settlementPrice')}
                name="settlementPrice"
                placeholder={DOLLAR}
                format={addDollarPreffix}
                parse={extractValueFromMoneyString}
                component={InputField}
                validate={validations.validDecimalNumbersOnlyValidation(
                  t('numbersOnlyError'),
                )}
              />
            </>
          )}

          {stage ? (
            <Button
              className="confirm-status-button"
              onClick={() => {
                if (invalid) {
                  submit();
                  return;
                }
                setStep(2);
              }}
            >
              {t('confirmStatusChange')}
            </Button>
          ) : (
            <Button
              variant="link"
              className="lost-mandate-button"
              icon={XcircleIcon}
              onClick={() => {
                change('stage', DEAL_STAGE.LOST);
                setStep(2);
              }}
            >
              {t('lostMandate')}
            </Button>
          )}
        </>
      ) : (
        <>
          <Field
            name="message"
            component={TextAreaField}
            label={t('sendAllYourLeadsMessage')}
            rows={5}
          />

          <BrokerPulsCheckActions />
        </>
      )}
    </div>
  );
};

export default NoLongerForSaleSection;
